import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';

const ContentRunReviewPage = lazy(() => import('~/features/content/pages/content-run-review-page'));

// The open product lives in the URL so a link (or a reload) lands on it.
const reviewSearchSchema = z.object({
  product: z.string().optional().catch(undefined),
  // Card (one product at a time, the default) or Grid (every product as a card).
  view: z.enum(['card', 'grid']).optional().catch(undefined),
  // Review list filters (large jobs) — in the URL so a filtered view can be shared.
  q: z.string().max(100).optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  qc: z.enum(['ANY', 'HIGH', 'MEDIUM', 'SUGGESTION', 'NONE']).optional().catch(undefined),
  rule: z.string().optional().catch(undefined),
  status: z.enum(['TODO', 'IN_PROGRESS', 'REVIEWED']).optional().catch(undefined),
  match: z.string().optional().catch(undefined),
  unsure: z.boolean().optional().catch(undefined),
  sort: z.enum(['FLAGS', 'LEAST_REVIEWED', 'PRODUCT']).optional().catch(undefined),
  // One of the run's batches (0-based index); shown as "Batch n+1".
  batch: z.coerce.number().int().min(0).optional().catch(undefined),
});

/** Attribution review workbench for one run at `/content/runs/$runId/review`. */
export const Route = createFileRoute('/_authenticated/_tenant/content/runs/$runId_/review')({
  validateSearch: reviewSearchSchema,
  component: RunReviewRoute,
});

function RunReviewRoute() {
  const { runId } = Route.useParams();
  const { product, view, q, type, qc, rule, status, match, unsure, sort, batch } =
    Route.useSearch();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ContentRunReviewPage
        runId={runId}
        productId={product}
        // Grid by default (the whole job at a glance); a link to one product opens it in Card view.
        view={view ?? (product ? 'card' : 'grid')}
        filters={{
          q,
          vertical: type,
          qc,
          checkName: rule,
          status,
          categoryResolution: match,
          lowConfidence: unsure,
          sort,
          batch,
        }}
      />
    </Suspense>
  );
}
